import {
  Directive,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

// inspired from https://css-tricks.com/using-css-transitions-auto-dimensions/

export type CollapseDirection = 'x' | 'y' | 'both' | 'none';

@Directive({
  selector: '[scCollapse]',
  standalone: true,
})
export class CollapseElementDirective implements OnChanges {
  @Input() scCollapse = false;
  @Input() collapseDirection: CollapseDirection = 'both';
  @Input() reboundYOnCollapse = 0;
  @Input() reboundXOnCollapse = 0;
  @Input() reboundOnCollapseTransitionTime = '0.2s';
  @Input() reboundYOnExpand = 0;
  @Input() reboundXOnExpand = 0;
  @Input() reboundOnExpandTransitionTime = '0.2s';

  private readonly elStyle: CSSStyleDeclaration;

  constructor(private readonly el: ElementRef) {
    this.elStyle = getComputedStyle(this.el.nativeElement);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.scCollapse) {
      this.toggleCollapse();
    }
  }

  toggleCollapse() {
    if (this.scCollapse) {
      this.applyTransitionOnElement(
        this.reboundXOnCollapse,
        this.reboundYOnCollapse,
        this.reboundOnCollapseTransitionTime
      );
    } else {
      this.applyTransitionOnElement(
        this.reboundXOnExpand,
        this.reboundYOnExpand,
        this.reboundOnExpandTransitionTime
      );
    }
  }

  async nextAnimationFrame() {
    return new Promise<void>((resolve) =>
      requestAnimationFrame(() => resolve())
    );
  }

  async nextTransitionEnd() {
    return new Promise<void>((resolve) => {
      const transitionEndListener = () => {
        // remove this event listener so it only gets triggered once
        this.el.nativeElement.removeEventListener(
          'transitionend',
          transitionEndListener
        );

        // End promise after transitionend listener has finished and is detached
        resolve();
      };

      // when the next css transition finishes (which should be the one we just triggered)
      this.el.nativeElement.addEventListener(
        'transitionend',
        transitionEndListener
      );
    });
  }

  assignNewSize(newHeight?: number | string, newWidth?: number | string) {
    if (this.collapseDirection === 'y' || this.collapseDirection === 'both') {
      if (typeof newHeight === 'number') {
        this.el.nativeElement.style.height = `${newHeight}px`;
      } else if (newHeight !== undefined) {
        this.el.nativeElement.style.height = newHeight;
      }
    }

    if (this.collapseDirection === 'x' || this.collapseDirection === 'both') {
      if (typeof newWidth === 'number') {
        this.el.nativeElement.style.width = `${newWidth}px`;
      } else if (newWidth !== undefined) {
        this.el.nativeElement.style.width = newWidth;
      }
    }
  }

  async applyTransitionOnElement(
    reboundX: number,
    reboundY: number,
    reboundTransitionTime: string
  ) {
    if (this.collapseDirection === 'none') {
      return;
    }

    const heightBeforeTransitionStr = this.elStyle.getPropertyValue('height');
    const widthBeforeTransitionStr = this.elStyle.getPropertyValue('width');

    // temporarily disable all css transitions
    const elementTransition = this.el.nativeElement.style.transition;
    this.el.nativeElement.style.transition = '';

    // on the next frame (as soon as the previous style change has taken effect),
    // explicitly set the element's height to its current pixel height, so we
    // aren't transitioning out of 'auto'
    await this.nextAnimationFrame();

    const heightAfterTransitionStr = this.elStyle.getPropertyValue('height');
    const widthAfterTransitionStr = this.elStyle.getPropertyValue('width');

    this.assignNewSize(heightBeforeTransitionStr, widthBeforeTransitionStr);
    this.el.nativeElement.style.transition = elementTransition;

    // on the next frame (as soon as the previous style change has taken effect),
    // have the element transition to height: <collapsed> - <rebound>
    await this.nextAnimationFrame();

    this.assignNewSize(
      parseInt(heightAfterTransitionStr) - reboundY,
      parseInt(widthAfterTransitionStr) - reboundX
    );

    await this.nextTransitionEnd();

    // after the transition with rebound, have the element transition to height: <collapsed>
    if (reboundX || reboundY) {
      const oldTransitionTime =
        this.el.nativeElement.style['transition-duration'];
      this.el.nativeElement.style['transition-duration'] =
        reboundTransitionTime;

      await this.nextAnimationFrame();

      this.assignNewSize(heightAfterTransitionStr, widthAfterTransitionStr);

      await this.nextTransitionEnd();

      this.el.nativeElement.style['transition-duration'] = oldTransitionTime;
    }

    this.assignNewSize(null, null);
  }
}
